/**
 * contextPrefill — pure helpers for the cross-module context prefill
 * (`field.prefillFromModule` + `field.prefillFrom`).
 *
 * A field can inherit its value from a DIFFERENT module's record than the form
 * is editing/creating — e.g. a submission field fed by the JOB it is raised
 * against. The two forms differ only in where the linked record's id comes
 * from:
 *
 *   AddFormV1   the URL linkage (?jobId= / ?candidateId= on Quick Submit)
 *   EditFormV1  the record's OWN stored ids, surfaced by getFormGroups as
 *               `recordMeta` (every top-level "*Id" key of the record)
 *
 * Both shapes are plain { someId: value } maps, so one resolver serves both.
 * No module or field name is hardcoded in either form — the alias table below
 * is the single place where a module's conventional id key is spelled out.
 */

import { getDeep } from './payloadTransformer';

// Extra id keys a module is known by, beyond the derived `<module>Id` /
// `<singular>Id`. Submissions/candidates historically link by "applicantId".
const EXTRA_ID_KEYS = {
    candidates: ['applicantId'],
};

/** Candidate id keys for a (normalized, plural) module key, most specific first. */
export function contextIdKeys(moduleKey) {
    const key = String(moduleKey ?? '').trim();
    if (!key) return [];
    const singular = key.endsWith('s') ? key.slice(0, -1) : key;
    return [...new Set([`${singular}Id`, `${key}Id`, ...(EXTRA_ID_KEYS[key] ?? [])])];
}

/**
 * Resolve the linked record id for `moduleKey` from a linkage/recordMeta map.
 * Values may be plain ids or { id } / { value } objects (recordMeta serializes
 * ObjectIDs as hex strings, but detail payloads sometimes carry objects).
 */
export function contextRecordId(moduleKey, source) {
    if (!source) return '';
    const idOf = (v) => (v && typeof v === 'object' ? (v.id ?? v.value ?? '') : (v ?? ''));
    for (const key of contextIdKeys(moduleKey)) {
        const id = String(idOf(source[key]) || '');
        if (id) return id;
    }
    return '';
}

/** Distinct normalized modules referenced by any field's prefillFromModule. */
export function contextPrefillModules(groups, normalizeModule) {
    const wanted = new Set();
    (groups ?? []).forEach((group) => (group.fields ?? []).forEach((field) => {
        if (field?.prefillFromModule) wanted.add(normalizeModule(field.prefillFromModule));
    }));
    return [...wanted];
}

/**
 * EDIT-side gate: which fields may a cross-module prefill write on an EXISTING
 * record?
 *
 * Only NON-PERSISTENT CARRIER fields (payloadMode "skip"). A carrier holds a
 * value that is never stored on the record, so there is nothing on the record
 * for it to contradict — it has to be re-derived on every edit or the rule it
 * feeds would be silently inert there. A field that IS stored keeps whatever
 * the record holds: re-pulling the source module's current value on edit would
 * silently overwrite what the user saved (e.g. a rate currency deliberately
 * changed after the job was raised). Config-driven, no field names.
 */
export function isNonPersistentCarrier(field) {
    return field?.payloadMode === 'skip';
}

/** getFormGroups responses come back in a few envelopes — normalize to an array. */
export function extractContextGroups(response) {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.groups)) return response.groups;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.groups)) return response.data.groups;
    return [];
}

/**
 * Flatten a form-groups response (scalar fields carry their stored `value`)
 * into { fieldKey: value }, indexed by both field key and payloadKey.
 */
export function flattenContextRecord(groups) {
    const record = {};
    (groups ?? []).forEach((group) => {
        if (group?.addRow) return;
        (group?.fields ?? []).forEach((field) => {
            if (!field?.field || field.value === undefined || field.value === null) return;
            record[field.field] = field.value;
            if (field.payloadKey && field.payloadKey !== field.field) record[field.payloadKey] = field.value;
        });
    });
    return record;
}

/** Read a field's value out of a context record: prefillFrom → field → payloadKey. */
export function pickContextValue(record, field) {
    if (!record || !field) return undefined;
    for (const path of [field.prefillFrom, field.field, field.payloadKey]) {
        if (!path) continue;
        let value = record[path];
        if (value === undefined || value === null) value = getDeep(record, path);
        if (value !== undefined && value !== null) return value;
    }
    return undefined;
}
